import { getECSobjPropVal } from '../helpers';
import { getMarketingId } from '../return-user';

/**
 * Observation store for Eppo assignment events.
 *
 * A raw boolean flag read cannot distinguish an assigned-control `false` from
 * the default/fallback `false` returned before Eppo has bucketed the subject.
 * The assignment event emitted through `assignmentLogger.logAssignment(...)` is
 * the strongest signal currently available that Eppo produced a real assignment,
 * so pages/hooks that must not stamp analytics from a fallback value observe
 * these events instead of waiting a blind stabilization delay.
 *
 * Assignments are also cached in `localStorage`, scoped by flag key + current
 * subject, so they persist for as long as the subject is unchanged. Eppo
 * de-duplicates assignment logs: on a refresh/second page load the assignment
 * is already known but no new experimentation log is emitted.
 */

export interface ObservedAssignment {
  featureFlag: string;
  subject: string;
  variation: string;
  timestamp?: string;
}

type AssignmentListener = (assignment: ObservedAssignment) => void;

/** Shape of the subset of `IAssignmentEvent` this store consumes. */
interface AssignmentEventLike {
  featureFlag?: string;
  subject?: string;
  variation?: string | null;
  timestamp?: string;
}

const STORAGE_PREFIX = 'ecs.eppo.assignment.';

const listeners = new Set<AssignmentListener>();
const observed = new Map<string, ObservedAssignment>();

/**
 * Current flag subject, mirroring `resolveFlagUser()`: marketing id first, then
 * `ecsstaticid`. Returns an empty string when neither is available yet.
 */
export function getAssignmentSubject(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return String(getMarketingId() || getECSobjPropVal('ecsstaticid') || '');
  } catch {
    return '';
  }
}

function cacheKey(featureFlag: string, subject: string): string {
  return `${STORAGE_PREFIX}${featureFlag}|${subject}`;
}

function readSession(featureFlag: string, subject: string): ObservedAssignment | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(cacheKey(featureFlag, subject));

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { storedAt?: number; assignment?: ObservedAssignment };

    return parsed?.assignment ?? null;
  } catch {
    // Storage is best effort only; never break flag evaluation.
    return null;
  }
}

function writeSession(assignment: ObservedAssignment): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      cacheKey(assignment.featureFlag, assignment.subject),
      JSON.stringify({ storedAt: Date.now(), assignment })
    );
  } catch {
    // Storage is best effort only (private mode/quota); ignore failures.
  }
}

/** Records an Eppo assignment event and notifies subscribers. */
export function recordAssignment(event: AssignmentEventLike): void {
  try {
    const featureFlag = event?.featureFlag;
    const subject = event?.subject;

    if (!featureFlag || !subject) {
      return;
    }

    const assignment: ObservedAssignment = {
      featureFlag,
      subject,
      variation: event.variation ?? '',
      timestamp: event.timestamp
    };

    observed.set(cacheKey(featureFlag, subject), assignment);
    writeSession(assignment);

    listeners.forEach((listener) => {
      try {
        listener(assignment);
      } catch {
        // A misbehaving subscriber must not break assignment logging.
      }
    });
  } catch {
    // Assignment observation must never break flag evaluation.
  }
}

/** Subscribes to observed assignment events. Returns an unsubscribe function. */
export function subscribeToAssignments(listener: AssignmentListener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Returns the assignment observed for `featureFlag` and the current subject,
 * from this page load or from the cache.
 */
export function getObservedAssignment(featureFlag: string): ObservedAssignment | null {
  const subject = getAssignmentSubject();

  if (!featureFlag || !subject) {
    return null;
  }

  const key = cacheKey(featureFlag, subject);
  const inMemory = observed.get(key);

  if (inMemory) {
    return inMemory;
  }

  const cached = readSession(featureFlag, subject);

  if (cached) {
    observed.set(key, cached);
  }

  return cached;
}

/** Whether an assignment has been observed for this flag and current subject. */
export function hasObservedAssignment(featureFlag: string): boolean {
  return getObservedAssignment(featureFlag) !== null;
}

/**
 * Boolean value of the observed assignment, when the variation can be read as a
 * boolean. Returns null for non-boolean variations or when nothing is observed.
 */
export function getObservedBooleanAssignment(featureFlag: string): boolean | null {
  const assignment = getObservedAssignment(featureFlag);

  if (!assignment) {
    return null;
  }

  const variation = String(assignment.variation).trim().toLowerCase();

  if (variation === 'true' || variation === 'on' || variation === '1') {
    return true;
  }

  if (variation === 'false' || variation === 'off' || variation === '0') {
    return false;
  }

  return null;
}
